/**
 * Comment minimap (F5c) — the whole page, small, with the comments on it.
 *
 * The CRM's version rasterizes its design `<img>` into a canvas. That is not
 * available here: the preview iframe is sandboxed WITHOUT allow-same-origin,
 * so `contentDocument` is null and nothing client-side can capture it.
 *
 * What exists instead is the server capture path the visual diff already uses
 * — `ensurePageShot` bundles the page to standalone HTML, renders it
 * headlessly and caches the PNG in R2 under (project, VERSION, page). That key
 * is the freshness contract: a save bumps the version, which is a new key, so
 * a shot can never silently belong to different content. Between an edit and
 * the next save the shot is older than the canvas, and the card SAYS so rather
 * than quietly showing the wrong page.
 *
 * When there is no shot — no capture backend, an unsaved project, a version
 * not captured yet — it falls back to a SCHEMATIC: one block per section,
 * heights proportional to the real ones, drawn from the positions feed that is
 * already flowing. The CRM ships a schematic fallback for the same reason.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { bridge } from '../editing/bridge.js';
import { reportError } from '../core/report-error.js';
import { isLocalOnlyPath } from '../shared/collab-protocol.js';
import { getActiveCommentThread } from './comment-controller.js';
import { railDotsFrom } from './comment-rail.js';

const MAP_WIDTH = 160;
const MAP_MAX_HEIGHT = 280;

/**
 * Where a shot may be used at all, and what to say when it may not.
 * Kept pure so the freshness rule is testable without a network or a store.
 *
 * @returns {{use: boolean, reason: 'ok'|'no-project'|'unsaved'|'unavailable'}}
 */
export function shotEligibility({ projectId, version, dirty, unavailable }) {
  if (unavailable) return { use: false, reason: 'unavailable' };
  if (!projectId || !version) return { use: false, reason: 'no-project' };
  // A shot is keyed by version. With unsaved edits the newest shot predates
  // them, so showing it would be showing a page that no longer exists.
  if (dirty) return { use: false, reason: 'unsaved' };
  return { use: true, reason: 'ok' };
}

/** Section rectangles as percentages — the schematic's whole geometry. */
export function schematicBlocks(positions, contentHeight) {
  if (!Array.isArray(positions) || !(contentHeight > 0)) return [];
  return positions
    .filter((p) => Number.isFinite(p?.top) && Number.isFinite(p?.height))
    .map((p) => ({
      label: p.label || 'Section',
      shared: !!p.shared,
      top: Math.max(0, Math.min(100, (p.top / contentHeight) * 100)),
      height: Math.max(0.5, Math.min(100, (p.height / contentHeight) * 100))
    }));
}

/** Only non-local state patches can make a version-keyed shot stale. */
export function patchesTouchShotContent(patches) {
  if (!Array.isArray(patches) || patches.length === 0) return true;
  return patches.some((patch) => !isLocalOnlyPath(patch?.path));
}

/**
 * Mount the minimap. The comments panel owns its lifetime, exactly like the rail.
 * @param {{onActivate: (threadId: string) => void, loadShot?: Function}} deps
 */
export function mountCommentMinimap({ onActivate, loadShot = null } = {}) {
  const card = document.createElement('div');
  card.className = 'pb-comment-minimap';
  card.hidden = true;

  const head = document.createElement('div');
  head.className = 'pb-comment-minimap-head';
  const title = document.createElement('span');
  title.className = 'pb-comment-minimap-title';
  title.textContent = 'Map';
  head.appendChild(title);
  const note = document.createElement('span');
  note.className = 'pb-comment-minimap-note';
  head.appendChild(note);
  card.appendChild(head);

  const body = document.createElement('div');
  body.className = 'pb-comment-minimap-body';
  body.style.width = `${MAP_WIDTH}px`;
  card.appendChild(body);

  const shotImg = document.createElement('img');
  shotImg.className = 'pb-comment-minimap-shot';
  shotImg.alt = '';
  shotImg.hidden = true;
  body.appendChild(shotImg);

  const schematic = document.createElement('div');
  schematic.className = 'pb-comment-minimap-schematic';
  body.appendChild(schematic);

  const dotsWrap = document.createElement('div');
  dotsWrap.className = 'pb-comment-minimap-dots';
  body.appendChild(dotsWrap);

  const viewport = document.createElement('div');
  viewport.className = 'pb-comment-minimap-viewport';
  viewport.hidden = true;
  body.appendChild(viewport);

  document.body.appendChild(card);

  let _feed = { positions: [], contentHeight: 0, viewportHeight: 0, scrollY: 0 };
  let _sections = [];
  let _clusters = [];
  let _objectUrl = null;
  let _shotSeq = 0;
  let _shotKey = null;
  let _controller = null;
  let _versionKey = versionKeyFromState(store.getState());
  let _contentDirty = currentDirtyTouchesShotContent();

  function versionKeyFromState(state) {
    const projectId = state?.projectId || null;
    const version = Number(state?.cloudVersion) || 0;
    return projectId && version ? `${projectId}:${version}` : null;
  }

  function currentDirtyTouchesShotContent() {
    if (!(store.isDirty?.() ?? false)) return false;
    const history = store.history;
    const latest = history?.entries?.[history.position];
    if (latest?.forward && !patchesTouchShotContent(latest.forward)) return false;
    return true;
  }

  function releaseShot() {
    if (_objectUrl) {
      URL.revokeObjectURL(_objectUrl);
      _objectUrl = null;
    }
    shotImg.removeAttribute('src');
    shotImg.hidden = true;
  }

  function invalidateShotRequest() {
    _shotSeq += 1;
    _controller?.abort();
    _controller = null;
  }

  function showOutline(reason) {
    releaseShot();
    _shotKey = null;
    note.textContent = reason === 'unsaved' ? 'outline · unsaved edits' : 'outline';
    note.dataset.tooltip =
      reason === 'unsaved'
        ? 'The last snapshot predates your unsaved edits, so the outline is shown instead.'
        : 'No page snapshot available — showing the section outline.';
  }

  /** Fetch (or capture) the shot for the CURRENT version, once per key. */
  async function refreshShot() {
    const state = store.getState();
    const projectId = state?.projectId || null;
    const version = Number(state?.cloudVersion) || 0;
    const pageId = state?.activePage || null;
    const nextVersionKey = versionKeyFromState(state);
    if (nextVersionKey && nextVersionKey !== _versionKey) {
      _versionKey = nextVersionKey;
      _contentDirty = false;
    }
    const eligibility = shotEligibility({
      projectId,
      version,
      dirty: _contentDirty,
      unavailable: !loadShot
    });

    if (!eligibility.use) {
      invalidateShotRequest();
      showOutline(eligibility.reason);
      return;
    }

    const key = `${projectId}:${version}:${pageId}`;
    if (key === _shotKey && _objectUrl) return; // already showing exactly this
    const seq = ++_shotSeq;
    _controller?.abort();
    const controller = new AbortController();
    _controller = controller;
    releaseShot();
    note.textContent = 'loading…';
    delete note.dataset.tooltip;

    try {
      const blob = await loadShot({ projectId, version, pageId, signal: controller.signal });
      // Three guards, not one: a newer request may have started (seq), the
      // project may have moved on while this was in flight (key), or the same
      // version may have become dirty while a cache hit kept resolving anyway.
      if (seq !== _shotSeq) return;
      const latestState = store.getState();
      const stillCurrent = `${latestState?.projectId}:${Number(latestState?.cloudVersion) || 0}:${latestState?.activePage}`;
      const stillEligible = shotEligibility({
        projectId: latestState?.projectId || null,
        version: Number(latestState?.cloudVersion) || 0,
        dirty: _contentDirty,
        unavailable: !loadShot
      });
      if (stillCurrent !== key || !stillEligible.use || !blob) {
        showOutline(stillEligible.reason);
        return;
      }
      releaseShot();
      _objectUrl = URL.createObjectURL(blob);
      shotImg.src = _objectUrl;
      shotImg.hidden = false;
      _shotKey = key;
      note.textContent = `v${version}`;
      note.dataset.tooltip = `Snapshot of version ${version}`;
    } catch (err) {
      if (err?.name === 'AbortError' || seq !== _shotSeq) return;
      showOutline('unavailable');
      reportError('Comment minimap', err, {
        user: 'silent',
        detail: 'page shot unavailable; falling back to the schematic',
        id: 'comment-minimap-shot'
      });
    } finally {
      if (_controller === controller) _controller = null;
    }
  }

  function paintSchematic() {
    schematic.textContent = '';
    // The outline is always drawn; the shot simply covers it when present, so
    // a slow or failed image never leaves an empty card.
    for (const block of _sections) {
      const el = document.createElement('div');
      el.className = 'pb-comment-minimap-block';
      el.classList.toggle('is-shared', block.shared);
      el.style.top = `${block.top}%`;
      el.style.height = `${block.height}%`;
      el.dataset.tooltip = block.label;
      schematic.appendChild(el);
    }
  }

  function paintDots() {
    const activeId = getActiveCommentThread();
    _clusters = railDotsFrom(_feed.positions, _feed.contentHeight);
    dotsWrap.textContent = '';
    for (const cluster of _clusters) {
      // pb-sweep-exempt: no-hand-rolled-button — a minimap dot is positioned chrome (percent-placed, count-labeled), not a kit action button; it carries its own aria-label and has no busy/async lifecycle
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pb-comment-minimap-dot';
      dot.style.top = `${cluster.pct}%`;
      dot.classList.toggle('is-resolved', !cluster.hasOpen);
      dot.classList.toggle('is-active', cluster.members.includes(activeId));
      dot.setAttribute(
        'aria-label',
        cluster.members.length > 1 ? `${cluster.members.length} comments` : 'Comment'
      );
      dot.addEventListener('click', (e) => {
        e.stopPropagation(); // a dot click is not a scrub
        onActivate?.(cluster.members[0]);
      });
      dotsWrap.appendChild(dot);
    }
  }

  function paintViewport() {
    const { scrollY, viewportHeight, contentHeight } = _feed;
    if (!(contentHeight > 0) || !(viewportHeight > 0)) {
      viewport.hidden = true;
      return;
    }
    viewport.hidden = false;
    viewport.style.top = `${Math.max(0, Math.min(100, (scrollY / contentHeight) * 100))}%`;
    viewport.style.height = `${Math.max(3, Math.min(100, (viewportHeight / contentHeight) * 100))}%`;
  }

  function sizeBody() {
    const { contentHeight } = _feed;
    // Aspect from the real page, capped — a very long page would otherwise
    // produce a card taller than the window. Dots stay correct either way
    // because everything inside is positioned in PERCENT.
    //
    // The iframe ELEMENT's width is readable from the parent (its document is
    // not), and contentHeight is already in the same visual space.
    const frame = document.querySelector('.pb-iframe-wrap iframe');
    const pageWidth = frame?.clientWidth || 1440;
    const aspect = contentHeight > 0 ? contentHeight / pageWidth : 1.6;
    body.style.height = `${Math.max(60, Math.min(MAP_MAX_HEIGHT, Math.round(MAP_WIDTH * aspect)))}px`;
  }

  /** Click or drag anywhere on the map scrolls the canvas there. */
  function scrubTo(clientY) {
    const rect = body.getBoundingClientRect();
    if (!rect.height || !(_feed.contentHeight > 0)) return;
    const pct = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    // Centre the viewport on the point rather than putting it at the top.
    const target = pct * _feed.contentHeight - _feed.viewportHeight / 2;
    bridge.scrollToVisualY(Math.max(0, target), { smooth: false });
  }

  let dragging = false;
  body.addEventListener('pointerdown', (e) => {
    if (e.target.closest('.pb-comment-minimap-dot')) return;
    dragging = true;
    body.setPointerCapture?.(e.pointerId);
    scrubTo(e.clientY);
  });
  body.addEventListener('pointermove', (e) => {
    if (dragging) scrubTo(e.clientY);
  });
  const endDrag = () => {
    dragging = false;
  };
  body.addEventListener('pointerup', endDrag);
  body.addEventListener('pointercancel', endDrag);

  function onPositions(feed = {}) {
    _feed = {
      positions: Array.isArray(feed.positions) ? feed.positions : [],
      contentHeight: Number(feed.contentHeight) || 0,
      viewportHeight: Number(feed.viewportHeight) || 0,
      scrollY: Number(feed.scrollY) || 0
    };
    card.hidden = !(_feed.contentHeight > 0);
    sizeBody();
    paintDots();
    paintViewport();
  }

  const offs = [
    eventBus.on('comments:pin-positions', onPositions),
    eventBus.on('iframe:section-positions', (data) => {
      _sections = schematicBlocks(data?.positions, Number(data?.contentHeight) || 0);
      paintSchematic();
    }),
    eventBus.on('comments:active-thread', paintDots),
    // A save bumps the version, which is a new shot key — re-resolve.
    eventBus.on('state-changed', () => void refreshShot()),
    eventBus.on('history-changed', ({ patches } = {}) => {
      const touchesShotContent = Array.isArray(patches)
        ? patchesTouchShotContent(patches)
        : currentDirtyTouchesShotContent();
      if (!touchesShotContent) return;
      _contentDirty = true;
      void refreshShot();
    }),
    eventBus.on('autosave:saved', () => void refreshShot()),
    eventBus.on('save:completed', () => void refreshShot())
  ];

  void refreshShot();

  return {
    destroy() {
      for (const off of offs) if (typeof off === 'function') off();
      _shotSeq += 1; // invalidate anything still in flight
      _controller?.abort();
      releaseShot();
      card.remove();
    }
  };
}
